// Weather and ocean data integration service

// Convert ZIP code to coordinates using a free geocoding service
export const zipToCoords = async (zipCode) => {
  try {
    const response = await fetch(`https://api.zippopotam.us/us/${zipCode}`);
    if (!response.ok) {
      throw new Error('Invalid ZIP code');
    }
    const data = await response.json();
    return {
      lat: parseFloat(data.places[0].latitude),
      lon: parseFloat(data.places[0].longitude),
      city: data.places[0]['place name'],
      state: data.places[0]['state abbreviation']
    };
  } catch (error) {
    console.error('Error converting ZIP to coordinates:', error);
    throw error;
  }
};

// Fetch weather data from Open-Meteo (with hourly data for morning/afternoon split)
export const fetchWeatherData = async (lat, lon, timezone = 'auto') => {
  try {
    const params = new URLSearchParams({
      latitude: lat,
      longitude: lon,
      daily: [
        'temperature_2m_max',
        'temperature_2m_min',
        'precipitation_probability_max',
        'wind_speed_10m_max',
        'weather_code',
        'cloud_cover_mean',
        'uv_index_max'
      ].join(','),
      hourly: [
        'temperature_2m',
        'precipitation_probability',
        'wind_speed_10m',
        'cloud_cover',
        'weather_code',
        'relative_humidity_2m'
      ].join(','),
      current: 'temperature_2m,relative_humidity_2m,weather_code,precipitation,wind_speed_10m,cloud_cover',
      temperature_unit: 'fahrenheit',
      wind_speed_unit: 'mph',
      precipitation_unit: 'inch',
      timezone: timezone, // Use location's timezone
      forecast_days: 7
    });

    const url = `https://api.open-meteo.com/v1/forecast?${params}`;
    console.log('🌤️ Fetching weather data from Open-Meteo...');

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Weather API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Weather data received');
    return data;
  } catch (error) {
    console.error('❌ Weather API error:', error);
    throw error;
  }
};

// Fetch ocean data from Stormglass.io
export const fetchOceanData = async (lat, lon) => {
  const apiKey = process.env.REACT_APP_STORMGLASS_API_KEY;

  if (!apiKey || apiKey === 'your_stormglass_key_here') {
    console.warn('⚠️ Stormglass API key not configured, skipping ocean data');
    return null;
  }

  try {
    const params = new URLSearchParams({
      lat: lat,
      lng: lon,
      params: 'waveHeight,wavePeriod,swellDirection,waterTemperature',
      source: 'noaa'
    });

    const url = `https://api.stormglass.io/v2/weather/point?${params}`;
    console.log('🌊 Fetching ocean data from Stormglass...');

    const response = await fetch(url, {
      headers: {
        'Authorization': apiKey
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ Stormglass API error:', response.status, errorData);

      if (response.status === 402) {
        console.warn('⚠️ Stormglass API: Payment required (over quota). Continuing without ocean data.');
      } else if (response.status === 429) {
        console.warn('⚠️ Stormglass API: Rate limit exceeded. Continuing without ocean data.');
      }

      return null;
    }

    const data = await response.json();
    console.log('✅ Ocean data received');
    return data;
  } catch (error) {
    console.error('❌ Ocean API error:', error);
    return null; // Don't fail the whole request if ocean data fails
  }
};

// Get weather description from weather code
export const getWeatherDescription = (weatherCode) => {
  if (weatherCode === 0) return 'Clear sky';
  if (weatherCode <= 1) return 'Mainly clear';
  if (weatherCode <= 2) return 'Partly cloudy';
  if (weatherCode <= 3) return 'Overcast';
  if (weatherCode <= 45) return 'Foggy';
  if (weatherCode <= 48) return 'Rime fog';
  if (weatherCode <= 51) return 'Light drizzle';
  if (weatherCode <= 55) return 'Drizzle';
  if (weatherCode <= 61) return 'Light rain';
  if (weatherCode <= 65) return 'Rain';
  if (weatherCode <= 67) return 'Freezing rain';
  if (weatherCode <= 75) return 'Snow';
  if (weatherCode <= 77) return 'Snow grains';
  if (weatherCode <= 82) return 'Rain showers';
  if (weatherCode <= 86) return 'Snow showers';
  if (weatherCode <= 95) return 'Thunderstorm';
  return 'Unknown';
};

// Get time period data (morning or afternoon) from hourly data
export const getTimePeriodData = (weatherData, oceanData, dayIndex, period = 'morning') => {
  const hourly = weatherData.hourly;
  const hoursPerDay = 24;
  const dayStartIndex = dayIndex * hoursPerDay;

  // Define time ranges: morning (8am-12pm), afternoon (1pm-5pm)
  const timeRanges = {
    morning: [8, 9, 10, 11, 12],
    afternoon: [13, 14, 15, 16, 17]
  };

  const hours = timeRanges[period];
  const indices = hours.map(h => dayStartIndex + h);

  // Average the hourly data for this period
  const avgTemp = indices.reduce((sum, i) => sum + (hourly.temperature_2m[i] || 0), 0) / hours.length;
  const avgPrecip = indices.reduce((sum, i) => sum + (hourly.precipitation_probability[i] || 0), 0) / hours.length;
  const avgWind = indices.reduce((sum, i) => sum + (hourly.wind_speed_10m[i] || 0), 0) / hours.length;
  const avgCloud = indices.reduce((sum, i) => sum + (hourly.cloud_cover[i] || 0), 0) / hours.length;
  const avgHumidity = indices.reduce((sum, i) => sum + (hourly.relative_humidity_2m[i] || 0), 0) / hours.length;

  // Use the most common weather code for the period
  const weatherCodes = indices.map(i => hourly.weather_code[i]).filter(Boolean);
  const weatherCode = weatherCodes[Math.floor(weatherCodes.length / 2)] || 0;

  const periodData = {
    date: weatherData.daily.time[dayIndex],
    period: period,
    periodLabel: period === 'morning' ? 'Morning (8am-12pm)' : 'Afternoon (1pm-5pm)',
    tempAvg: avgTemp,
    precipProb: avgPrecip,
    windSpeed: avgWind,
    cloudCover: avgCloud,
    humidity: avgHumidity,
    weatherCode: weatherCode,
    weatherDescription: getWeatherDescription(weatherCode),
  };

  // Add ocean data if available (same as full day)
  if (oceanData && oceanData.hours && oceanData.hours[dayIndex * 4]) {
    const oceanSample = oceanData.hours[dayIndex * 4];
    periodData.waveHeight = oceanSample.waveHeight?.noaa || null;
    periodData.wavePeriod = oceanSample.wavePeriod?.noaa || null;
    // Stormglass returns water temp in Celsius - convert to Fahrenheit
    periodData.waterTemp = oceanSample.waterTemperature?.noaa
      ? (oceanSample.waterTemperature.noaa * 9/5) + 32
      : null;
    periodData.swellDirection = oceanSample.swellDirection?.noaa || null;
  }

  return periodData;
};

// Calculate cloud cover average during beach hours (10am-5pm)
const getBeachHoursCloudCover = (weatherData, dayIndex) => {
  const hourly = weatherData.hourly;
  const daily = weatherData.daily;
  const startHour = dayIndex * 24 + 10; // 10am
  const endHour = dayIndex * 24 + 17;   // 5pm

  let sum = 0;
  let count = 0;

  for (let i = startHour; i <= endHour && i < hourly.time.length; i++) {
    if (hourly.cloud_cover[i] !== null && hourly.cloud_cover[i] !== undefined) {
      sum += hourly.cloud_cover[i];
      count++;
    }
  }

  return count > 0 ? sum / count : daily.cloud_cover_mean[dayIndex];
};

// Calculate precipitation probability average during beach hours (10am-5pm)
const getBeachHoursPrecipitation = (weatherData, dayIndex) => {
  const hourly = weatherData.hourly;
  const daily = weatherData.daily;
  const startHour = dayIndex * 24 + 10; // 10am
  const endHour = dayIndex * 24 + 17;   // 5pm

  let sum = 0;
  let count = 0;

  for (let i = startHour; i <= endHour && i < hourly.time.length; i++) {
    if (hourly.precipitation_probability[i] !== null && hourly.precipitation_probability[i] !== undefined) {
      sum += hourly.precipitation_probability[i];
      count++;
    }
  }

  return count > 0 ? sum / count : daily.precipitation_probability_max[dayIndex];
};

// Combine weather and ocean data for a specific day
export const getCombinedDayData = (weatherData, oceanData, dayIndex) => {
  const daily = weatherData.daily;

  const combined = {
    date: daily.time[dayIndex],
    tempMax: daily.temperature_2m_max[dayIndex],
    tempMin: daily.temperature_2m_min[dayIndex],
    precipProb: getBeachHoursPrecipitation(weatherData, dayIndex), // Only 10am-5pm
    windSpeed: daily.wind_speed_10m_max[dayIndex],
    weatherCode: daily.weather_code[dayIndex],
    cloudCover: getBeachHoursCloudCover(weatherData, dayIndex), // Only 10am-5pm
    uvIndex: daily.uv_index_max[dayIndex],
    weatherDescription: getWeatherDescription(daily.weather_code[dayIndex]),
    // Add morning and afternoon data
    morning: getTimePeriodData(weatherData, oceanData, dayIndex, 'morning'),
    afternoon: getTimePeriodData(weatherData, oceanData, dayIndex, 'afternoon'),
  };

  // Add ocean data if available
  if (oceanData && oceanData.hours && oceanData.hours[dayIndex * 4]) {
    // Sample ocean data every 6 hours (4 samples per day)
    const oceanSample = oceanData.hours[dayIndex * 4];
    combined.waveHeight = oceanSample.waveHeight?.noaa || null;
    combined.wavePeriod = oceanSample.wavePeriod?.noaa || null;
    // Stormglass returns water temp in Celsius - convert to Fahrenheit
    combined.waterTemp = oceanSample.waterTemperature?.noaa
      ? (oceanSample.waterTemperature.noaa * 9/5) + 32
      : null;
    combined.swellDirection = oceanSample.swellDirection?.noaa || null;
  }

  return combined;
};

// Fetch all data for a location
// Main function - accepts location object or ZIP code for backwards compatibility
export const fetchBeachDayData = async (locationOrZip) => {
  try {
    let coords;
    let locationName;

    // Check if it's a location object (new format) or ZIP code (legacy)
    if (typeof locationOrZip === 'object' && locationOrZip !== null) {
      // New format: location object with lat/lon
      coords = {
        lat: locationOrZip.latitude,
        lon: locationOrZip.longitude,
        city: locationOrZip.name,
        state: locationOrZip.state || locationOrZip.country
      };
      locationName = locationOrZip.shortName || locationOrZip.displayName;
    } else {
      // Legacy format: ZIP code string
      coords = await zipToCoords(locationOrZip);
      locationName = `${coords.city}, ${coords.state}`;
    }

    // Step 2: Fetch weather and ocean data in parallel
    const [weatherData, oceanData] = await Promise.all([
      fetchWeatherData(coords.lat, coords.lon),
      fetchOceanData(coords.lat, coords.lon)
    ]);

    // Step 3: Combine data for 7 days
    const forecast = [];
    for (let i = 0; i < 7; i++) {
      forecast.push(getCombinedDayData(weatherData, oceanData, i));
    }

    return {
      location: coords,
      locationName,
      current: {
        temp: weatherData.current?.temperature_2m,
        humidity: weatherData.current?.relative_humidity_2m,
        weatherCode: weatherData.current?.weather_code,
        precipitation: weatherData.current?.precipitation || 0,
        windSpeed: weatherData.current?.wind_speed_10m,
        cloudCover: weatherData.current?.cloud_cover,
        weatherDescription: getWeatherDescription(weatherData.current?.weather_code),
      },
      forecast,
      hasOceanData: oceanData !== null
    };
  } catch (error) {
    console.error('Error fetching beach day data:', error);
    throw error;
  }
};
